/**
 * Detect session partnership splits in a 4-player (or partial) roster.
 * Used for UI notices only — does not block matchmaking.
 */

function partnershipIds(partnership) {
  const p1 = partnership?.player1Id || partnership?.player1_id;
  const p2 = partnership?.player2Id || partnership?.player2_id;
  if (!p1 || !p2) return null;
  return [p1, p2];
}

/**
 * @returns {Array<{ type: 'lone_partner'|'opposite_teams', player1Id: string, player2Id: string, playerInGameId?: string, playerWaitingId?: string }>}
 */
export function findPartnershipSplitsInRoster(team1, team2, partnerships = []) {
  if (!partnerships?.length) return [];

  const team1Ids = team1 || [];
  const team2Ids = team2 || [];
  const roster = new Set([...team1Ids, ...team2Ids]);
  if (roster.size === 0) return [];

  const splits = [];

  for (const partnership of partnerships) {
    const ids = partnershipIds(partnership);
    if (!ids) continue;
    const [p1, p2] = ids;

    const p1In = roster.has(p1);
    const p2In = roster.has(p2);

    if (p1In !== p2In) {
      splits.push({
        type: 'lone_partner',
        player1Id: p1,
        player2Id: p2,
        playerInGameId: p1In ? p1 : p2,
        playerWaitingId: p1In ? p2 : p1,
      });
      continue;
    }

    if (p1In && p2In) {
      const p1Team1 = team1Ids.includes(p1);
      const p2Team1 = team1Ids.includes(p2);
      if (p1Team1 !== p2Team1) {
        splits.push({ type: 'opposite_teams', player1Id: p1, player2Id: p2 });
      }
    }
  }

  return splits;
}

function playerFirstName(playersById, playerId) {
  const name = playersById?.[playerId]?.name;
  if (!name) return 'Player';
  return name.trim().split(/\s+/)[0];
}

export function describePartnershipSplit(split, playersById) {
  if (split.type === 'lone_partner') {
    const inGame = playerFirstName(playersById, split.playerInGameId);
    const waiting = playerFirstName(playersById, split.playerWaitingId);
    return `${inGame} is in this game without partner ${waiting}`;
  }
  const n1 = playerFirstName(playersById, split.player1Id);
  const n2 = playerFirstName(playersById, split.player2Id);
  return `${n1} and ${n2} are on opposite teams`;
}

export function describePartnershipSplitsSummary(splits, playersById) {
  if (!splits?.length) return null;
  const parts = splits.map((split) => describePartnershipSplit(split, playersById));
  if (parts.length === 1) {
    return `Partnership split for this game: ${parts[0]}.`;
  }
  return `Partnership splits for this game: ${parts.join('; ')}.`;
}
